import type { IStoryDetail } from 'src/types/story';
import type { IEventDetail } from 'src/types/event';
import type { CreationResponse } from 'src/utils/axios';

import useSWR, { mutate } from 'swr';
import { useMemo, useState, useCallback } from 'react';

import { mutatePlaceList, useRevalidatePlaceImageCache } from 'src/utils/mutate-utils';
import {
  poster,
  deleter,
  fetcher,
  updater,
  getEndpoints,
  createEndpoints,
  deleteEndpoints,
  updateEndpoints,
} from 'src/utils/axios';

import { useWorkspaceContext } from 'src/components/workspace/use-workspace-context';

import { type IImage, type IImageForUpload, appendImageToFormData } from 'src/types/image';

import { onStoryMutated } from './story/story';
import { useGetAndUpdateUserContext } from './user';

// ----------------------------------------------------------------------

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

type ImagesData = {
  data: IImage[];
};

function useGetImages(endpointUrl: string | null) {
  const { data, isLoading, error, isValidating } = useSWR<ImagesData>(
    endpointUrl,
    endpointUrl ? fetcher : null,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      images: data?.data || [],
      imagesLoading: isLoading,
      imagesError: error,
      imagesValidating: isValidating,
      imagesEmpty: !isLoading && (!data?.data || data?.data.length === 0),
    }),
    [data?.data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetImagesForPlace(guid: string | undefined, type: string) {
  const url = guid ? getEndpoints.place.images(guid) : null;
  const result = useGetImages(url);
  return {
    ...result,
    images: [...result.images].sort((a, b) => a.index - b.index),
  };
}

// ---------------------------------------------------------------------------
// Image search (public-domain / beta)
// ---------------------------------------------------------------------------

export function usePlaceImageSearch() {
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchPlaceImage = useCallback(async (placeGuid: string) => {
    setIsSearching(true);
    setError(null);

    try {
      const endpoint = createEndpoints.place.imageSearch(placeGuid);
      const result = await poster(endpoint, {});
      return result.data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsSearching(false);
    }
  }, []);

  return useMemo(
    () => ({
      searchPlaceImage,
      isSearching,
      error,
    }),
    [searchPlaceImage, isSearching, error]
  );
}

export function useEventImageSearch() {
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchEventImage = useCallback(async (eventGuid: string) => {
    setIsSearching(true);
    setError(null);
    try {
      const endpoint = createEndpoints.event.imageSearch(eventGuid);
      const result = await poster(endpoint, {});
      return result.data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsSearching(false);
    }
  }, []);

  return useMemo(
    () => ({ searchEventImage, isSearching, error }),
    [searchEventImage, isSearching, error]
  );
}

//---------------------------------------------------------------------------
// CREATE IMAGE
//
//
//---------------------------------------------------------------------------

// Exported functions for different image types
export function useCreateImageForPlace(guid: string) {
  const { createImage, isCreating, error } = useCreateImage();
  const { orgGuid } = useWorkspaceContext();

  const createImageForPlace = useCallback(
    async (image: IImageForUpload) => {
      const endpoint = createEndpoints.place.image(guid);
      const result = await createImage(endpoint, image);
      mutate(getEndpoints.place.images(guid), { revalidate: true });
      mutatePlaceList(orgGuid);
      return result;
    },
    [guid, createImage, orgGuid]
  );

  return useMemo(
    () => ({
      createImageForPlace,
      isCreating,
      error,
    }),
    [createImageForPlace, isCreating, error]
  );
}

export function useUpdateImage() {
  const [isReplacing, setIsReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const replaceImage = useCallback(
    async (imageReq: IImageForUpload, currentImage: IImage, mediaType?: string) => {
      setIsReplacing(true);
      setError(null);

      try {
        const endpoint = updateEndpoints.image.image(currentImage.guid);
        const formData = new FormData();

        if (imageReq.file) appendImageToFormData(formData, imageReq, 'primaryImage');
        if (mediaType) formData.append('mediaType', mediaType);

        const result = await mutate(endpoint, updater(endpoint, formData), { revalidate: true });

        // The backend clears citations for an image whenever its content changes,
        // so drop the stale citations cache for this image guid.
        await mutate(getEndpoints.image.citations(currentImage.guid), undefined, {
          revalidate: true,
        });

        // TODO: revalidate the image

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsReplacing(false);
      }
    },
    []
  );

  const memoizedValue = useMemo(
    () => ({
      replaceImage,
      isReplacing,
      error,
    }),
    [replaceImage, isReplacing, error]
  );

  return memoizedValue;
}

export function useReplaceImageForStory(story: IStoryDetail) {
  const [isReplacing, setIsReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { orgGuid } = useWorkspaceContext();

  const replaceImage = useCallback(
    async (imageReq: IImageForUpload) => {
      setIsReplacing(true);
      setError(null);

      try {
        const endpoint = updateEndpoints.story.story(story.guid);
        const formData = new FormData();

        if (imageReq.file) appendImageToFormData(formData, imageReq, 'primaryImage');

        const result = await mutate(endpoint, updater(endpoint, formData), { revalidate: true });

        await onStoryMutated(story.guid, orgGuid, story.place.guid);

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsReplacing(false);
      }
    },
    [orgGuid, story]
  );

  const memoizedValue = useMemo(
    () => ({
      replaceImage,
      isReplacing,
      error,
    }),
    [replaceImage, isReplacing, error]
  );

  return memoizedValue;
}

export function useReplaceImageForEvent(event: IEventDetail) {
  const [isReplacing, setIsReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const replaceImage = useCallback(
    async (imageReq: IImageForUpload, currentImage: IImage) => {
      setIsReplacing(true);
      setError(null);

      try {
        const endpoint = updateEndpoints.image.image(currentImage.guid);
        const formData = new FormData();

        if (imageReq.file) appendImageToFormData(formData, imageReq, 'primaryImage');

        const result = await mutate(endpoint, updater(endpoint, formData), { revalidate: true });
        await mutate(getEndpoints.event.details(event.guid), { revalidate: true });

        // The backend clears citations for an image whenever its content changes,
        // so drop the stale citations cache for this image guid.
        await mutate(getEndpoints.image.citations(currentImage.guid), undefined, {
          revalidate: true,
        });

        return result.data;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsReplacing(false);
      }
    },
    [event]
  );

  const memoizedValue = useMemo(
    () => ({
      replaceImage,
      isReplacing,
      error,
    }),
    [replaceImage, isReplacing, error]
  );

  return memoizedValue;
}

export function useReplaceImageForPlace(placeGuid: string) {
  const [isReplacing, setIsReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { createImageForPlace } = useCreateImageForPlace(placeGuid);
  const { deleteImage } = useDeleteImage();
  const { mutatePlaceCache } = useRevalidatePlaceImageCache(placeGuid);

  const replaceImage = useCallback(
    async (imageReq: IImageForUpload, currentImage: IImage) => {
      setIsReplacing(true);
      setError(null);

      try {
        // replace is just create new and delete existing
        const newImage = await createImageForPlace(imageReq);
        await deleteImage(currentImage.guid);

        // Revalidate all place-related caches
        await mutatePlaceCache();

        return newImage;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setIsReplacing(false);
      }
    },
    [createImageForPlace, deleteImage, mutatePlaceCache]
  );

  const memoizedValue = useMemo(
    () => ({
      replaceImage,
      isReplacing,
      error,
    }),
    [replaceImage, isReplacing, error]
  );

  return memoizedValue;
}

export function useDeleteImageForPlace(placeGuid: string) {
  const { deleteImage, isDeleting, error } = useDeleteImage();
  const deleteImageForPlace = useCallback(
    async (imageGuid: string) => {
      if (!imageGuid || imageGuid === '') {
        throw new Error('Image GUID is required to delete image');
      }
      const result = await deleteImage(imageGuid);
      mutate(getEndpoints.place.images(placeGuid), { revalidate: true });
      return result;
    },
    [deleteImage, placeGuid]
  );

  return useMemo(
    () => ({
      deleteImageForPlace,
      isDeleting,
      error,
    }),
    [deleteImageForPlace, isDeleting, error]
  );
}

export function useCreateImageForStory(storyGuid: string) {
  const { createImage, isCreating, error } = useCreateImage();

  const createImageForStory = useCallback(
    async (image: IImageForUpload) => {
      const endpoint = createEndpoints.story.image(storyGuid);
      const result = await createImage(endpoint, image);
      return result;
    },
    [createImage, storyGuid]
  );

  return useMemo(
    () => ({
      createImageForStory,
      isCreating,
      error,
    }),
    [createImageForStory, isCreating, error]
  );
}

export function useCreateImageForEvent(eventGuid: string) {
  const { createImage, isCreating, error } = useCreateImage();

  const createImageForEvent = useCallback(
    async (image: IImageForUpload) => {
      const endpoint = createEndpoints.event.image(eventGuid);
      const result = await createImage(endpoint, image);
      mutate(getEndpoints.event.details(eventGuid), undefined, { revalidate: true });
      return result;
    },
    [createImage, eventGuid]
  );

  return useMemo(
    () => ({
      createImageForEvent,
      isCreating,
      error,
    }),
    [createImageForEvent, isCreating, error]
  );
}

function useCreateImage() {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createImage = useCallback(async (endpoint: string, image: IImageForUpload) => {
    setIsCreating(true);
    setError(null);

    try {
      // Create a FormData object and append image properties
      const formData = new FormData();
      appendImageToFormData(formData, image);

      // Send FormData to the provided endpoint
      const result = await mutate(endpoint, poster(endpoint, formData));

      return result.data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const memoizedValue = useMemo(
    () => ({
      createImage,
      isCreating,
      error,
    }),
    [createImage, isCreating, error]
  );

  return memoizedValue;
}

//---------------------------------------------------------------------------
// REORDER IMAGE INDICIES
//
//
//---------------------------------------------------------------------------

// Exported function for reordering images for a place
// the list of images here should be in the newly desired order
// you can either include all images that belong to this place OR just the images
// where the index will have changed.
export function useImageReorderForPlace(guid: string) {
  const { reorderImages, isReordering, error } = useImageReorder();

  const reorderImagesForPlace = useCallback(
    (images: IImage[]) => {
      const endpoint = updateEndpoints.place.imageReorder(guid);
      return reorderImages(endpoint, images);
    },
    [guid, reorderImages]
  );

  return useMemo(
    () => ({
      reorderImagesForPlace,
      isReordering,
      error,
    }),
    [reorderImagesForPlace, isReordering, error]
  );
}

function useImageReorder() {
  const [isReordering, setIsReordering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reorderImages = useCallback(async (endpoint: string, images: IImage[]) => {
    setIsReordering(true);
    setError(null);

    try {
      // Simplify the images list to the required JSON format
      const reorderedImages = images.map((image, index) => ({
        imageGUID: image.guid,
        index: image.index,
      }));

      // Send the simplified JSON to the provided endpoint
      const result: CreationResponse = await updater(endpoint, reorderedImages);

      return result.data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsReordering(false);
    }
  }, []);

  const memoizedValue = useMemo(
    () => ({
      reorderImages,
      isReordering,
      error,
    }),
    [reorderImages, isReordering, error]
  );

  return memoizedValue;
}

//---------------------------------------------------------------------------
// DELETE IMAGE
//
//
//---------------------------------------------------------------------------

export const useDeleteImageForUser = (userGuid: string, userId: number, imageGuid: string) => {
  const { deleteImage, isDeleting, error } = useDeleteImage();
  const { getUserAndUpdateContext } = useGetAndUpdateUserContext({
    userId,
  });

  const deleteImageForUser = useCallback(async () => {
    if (!imageGuid || imageGuid === '') {
      throw new Error('Image GUID is required to delete image');
    }
    if (!userGuid || userGuid === '') {
      throw new Error('User GUID is required to delete image');
    }
    const result = await deleteImage(imageGuid);

    // revalidate user - don't throw if this fails, image is already deleted
    try {
      await getUserAndUpdateContext();
    } catch (deleteError: any) {
      console.error('Failed to refresh user context after image deletion:', deleteError);
      // Don't throw - image deletion was successful
    }

    return result;
  }, [deleteImage, imageGuid, userGuid, getUserAndUpdateContext]);

  return useMemo(
    () => ({
      deleteImageForUser,
      isDeleting,
      error,
    }),
    [deleteImageForUser, isDeleting, error]
  );
};

export const useDeleteImageForOrganization = (orgGuid: string, imageGuid: string) => {
  const { deleteImage, isDeleting, error } = useDeleteImage();

  const deleteImageForOrganization = useCallback(async () => {
    if (!imageGuid || imageGuid === '') {
      throw new Error('Image GUID is required to delete image');
    }
    if (!orgGuid || orgGuid === '') {
      throw new Error('Organization GUID is required to delete image');
    }
    const result = await deleteImage(imageGuid);

    // revalidate organization
    await mutate(getEndpoints.organization.base(orgGuid), undefined, { revalidate: true });
    return result;
  }, [deleteImage, imageGuid, orgGuid]);

  return useMemo(
    () => ({
      deleteImageForOrganization,
      isDeleting,
      error,
    }),
    [deleteImageForOrganization, isDeleting, error]
  );
};

export function useDeleteImage() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function deleteImage(guid: string) {
    if (!guid) {
      throw new Error('Image GUID is required to delete image');
    }

    setIsDeleting(true);
    setError(null);

    try {
      // Send the form data to the appropriate endpoint
      const result = await mutate(
        deleteEndpoints.image.image(guid),
        deleter(deleteEndpoints.image.image(guid)),
        { revalidate: true }
      );

      return result.data;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }

  const memoizedValue = useMemo(
    () => ({
      deleteImage,
      isDeleting,
      error,
    }),
    [isDeleting, error]
  );

  return memoizedValue;
}

export function useRevalidateOrgCache(orgGuid: string) {
  const revalidateOrgCache = useCallback(async () => {
    await mutate(getEndpoints.organization.base(orgGuid));
    // Add any other endpoints that need refreshing
    return true;
  }, [orgGuid]);

  return { revalidateOrgCache };
}
